import { CommonModule } from '@angular/common';
import { LOCALE_ID, NgModule, StaticClassProvider } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { UtilsService } from 'src/app/core';
import { BillingsEffects } from 'src/app/core/store/billings/billings.effects';
import { billingsReducer } from 'src/app/core/store/billings/billings.reducer';
import { ApplyPlanFormComponent } from 'src/app/features/shared/apply-plan-form/apply-plan-form.component';
import { ViewComputerPipe } from 'src/app/features/shared/generic-table/pipes/view.pipe';
import { PlanSummaryComponent } from 'src/app/features/shared/plan-summary/plan-summary.component';
import { SelectPlanFormComponent } from 'src/app/features/shared/select-plan-form/select-plan-form.component';
import { SocialModule } from 'src/app/shared/components/social/social.module';
import { TablesModule } from 'src/app/shared/components/tables/tables.module';
import { DirectivesModule } from 'src/app/shared/directives/directives.modules';
import { PipesModule } from 'src/app/shared/pipes/pipes.module';

import { CopyTextComponent } from './copy-text/copy-text.component';
import { CountriesListInputComponent } from './countries-list-input/countries-list-input.component';
import { ExportButtonComponent } from './export-button/export-button.component';
import { FileUploaderComponent } from './file-uploader/file-uploader.component';
import { FormatAddressMxComponent } from './format-address/format-address-mx/format-address-mx.component';
import { FormatAddressUsComponent } from './format-address/format-address-us/format-address-us.component';
import { FormatAddressComponent } from './format-address/format-address.component';
import { GenericPaginationComponent } from './generic-pagination/generic-pagination.component';
import { GenericTableBodyRendererComponent } from './generic-table/generic-table-body-renderer/generic-table-body-renderer.component';
import { GenericTableHeaderRendererComponent } from './generic-table/generic-table-header-renderer/generic-table-header-renderer.component';
import { GenericTableComponent } from './generic-table/generic-table.component';
import { ValueComputerPipe } from './generic-table/pipes/value.pipe';
import { LabelFormComponent } from './label-form/label-form.component';
import { ListLinesByStatusComponent } from './list-lines-by-status/list-lines-by-status.component';
import { PhoneInputComponent } from './phone-input/phone-input.component';
import { SearchBarAutocompleteComponent } from './search-bar-autocomplete/search-bar-autocomplete.component';
import { SearchBarEndUserComponent } from './search-bar-end-user/search-bar-end-user.component';
import { SearchBarComponent } from './search-bar/search-bar.component';
import { StepReviewComponent } from './step-review/step-review.component';
import { TableHeaderSearchComponent } from './table-header-search/table-header-search.component';

class DynamicLocaleId extends String {
  constructor(protected service: TranslateService) {
    super('');
  }

  toString() {
    return this.service.currentLang;
  }
}

const LocaleProvider: StaticClassProvider = {
  provide: LOCALE_ID,
  useClass: DynamicLocaleId,
  deps: [TranslateService],
};

@NgModule({
  declarations: [
    FileUploaderComponent,
    GenericTableComponent,
    ValueComputerPipe,
    ViewComputerPipe,
    GenericPaginationComponent,
    ExportButtonComponent,
    SearchBarComponent,
    GenericTableHeaderRendererComponent,
    GenericTableBodyRendererComponent,
    PhoneInputComponent,
    CountriesListInputComponent,
    FormatAddressComponent,
    FormatAddressMxComponent,
    FormatAddressUsComponent,
    LabelFormComponent,
    CopyTextComponent,
    TableHeaderSearchComponent,
    SearchBarEndUserComponent,
    PlanSummaryComponent,
    SelectPlanFormComponent,
    ApplyPlanFormComponent,
    StepReviewComponent,
    ListLinesByStatusComponent,
    SearchBarAutocompleteComponent,
  ],
  imports: [
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    PipesModule,
    NzUploadModule,
    NzButtonModule,
    NzTableModule,
    NzTagModule,
    NzDropDownModule,
    NzPaginationModule,
    NzFormModule,
    NzInputModule,
    NzInputNumberModule,
    NzIconModule,
    NgxIntlTelInputModule,
    NzEmptyModule,
    NzToolTipModule,
    NzSpinModule,
    NzTypographyModule,
    NzAutocompleteModule,
    NzSelectModule,
    NzTypographyModule,
    NzAutocompleteModule,
    NzListModule,
    TablesModule,
    DirectivesModule,
    SocialModule,
    NzLayoutModule,
    NzCardModule,
    NzRadioModule,
    NzCheckboxModule,
    NzSpinModule,
    NzSelectModule,
    StoreModule.forFeature('billings', billingsReducer),
    EffectsModule.forFeature([BillingsEffects]),
  ],
  exports: [
    SearchBarEndUserComponent,
    FormatAddressComponent,
    PhoneInputComponent,
    FileUploaderComponent,
    GenericTableComponent,
    ValueComputerPipe,
    ViewComputerPipe,
    GenericPaginationComponent,
    ExportButtonComponent,
    SearchBarComponent,
    SearchBarAutocompleteComponent,
    GenericTableHeaderRendererComponent,
    GenericTableBodyRendererComponent,
    DirectivesModule,
    CountriesListInputComponent,
    LabelFormComponent,
    TableHeaderSearchComponent,
    CopyTextComponent,
    PlanSummaryComponent,
    SelectPlanFormComponent,
    ApplyPlanFormComponent,
    StepReviewComponent,
    ListLinesByStatusComponent,
  ],
  providers: [LocaleProvider, ValueComputerPipe, UtilsService],
})
export class FeatureSharedModule {}
